Pre<template>
  <div class="text-center" style="height: 100%">
    <v-form ref="form" v-model="dataValid">

      <template v-if="programmeSelectionPending">
        <v-alert type="warning" outlined class="mx-2 mx-md-10 mt-5 text-left">
          <div class="font-weight-bold mb-1">Select your final programme</div>
          <div>
            Your current programme is an option-selection programme. Choose an available programme in
            {{ currentDepartmentName }} before providing SSCE details or screening documents.
          </div>
        </v-alert>

        <v-row class="mx-0 mx-md-7">
          <v-col cols="12">
            <v-text-field
              :value="getUser.programme.name"
              label="Current programme"
              outlined
              dense
              readonly
            ></v-text-field>
            <v-autocomplete
              label="Final programme"
              :items="programmes"
              item-text="name"
              item-value="id"
              outlined
              dense
              :loading="loadingProgrammes"
              :disabled="loadingProgrammes || programmes.length === 0"
              no-data-text="No eligible programmes are currently available"
              v-model="obj.programmeId"
              required
              :rules="textRules"
            ></v-autocomplete>
          </v-col>
        </v-row>
      </template>

      <v-row v-else>
        <v-col>
          <div class="basic--text">
            <v-autocomplete class="mx-10 mt-5" :label="noHeader ? 'Course of study' : 'Programme of study'" :items="programmes" item-text="name"
              item-value="id" outlined dense :disabled="!canEdit || isUtme" v-model="obj.programmeId" required
              :rules="textRules"></v-autocomplete>
          </div>
        </v-col>
      </v-row>

      <div class="basic--text font-weight-bold" v-if="!noHeader || programmeSelectionPending">Passport (recent)</div>
      <v-alert v-if="programmeSelectionPending && !hasPassport" type="info" outlined class="mx-2 mx-md-10 mt-4 text-left">
        Upload a recent passport photograph before confirming your final programme.
      </v-alert>
      <div class="mx-auto" style="height: 100%;" v-if="!noHeader || programmeSelectionPending">
        <v-card elevation="0" rounded class="mx-auto mt-4">
          <v-img height="180" width="180" :src="image" class="mx-auto" v-if="!hidePhoto"></v-img>
        </v-card>
      </div>

      <div v-if="!noHeader || programmeSelectionPending">
        <v-btn color="primary" justify-center elevation="0" width="50%" class="mt-4 " style="text-align:center"
          @click.stop="showUploadPicture = !showUploadPicture" :disabled="!canEdit">Change
        </v-btn>
      </div>

      <div class="mt-3" v-if="!noHeader || programmeSelectionPending">
        <v-checkbox required :rules="textRules"
          v-model="declaration"
          :value="!canEdit" label="I hereby declare that particulars given in this form are
                                to best of my knowledge and belief correct, and that if admitted into the University,
                                I shall regard myself bound by Act Status and Regulations of the University as they affect me.
                                I also accept that the University reserves the right to withdraw admission made in error, and cancel my
                                admission or enrolment if it is subsequently discovered that I have given false information or withheld information to aid my admission.
                                The decision of the University on all matters pertaining to this application is final and no communication will be entertained from any
                                candidate whose application is unsuccessful"></v-checkbox>

      </div>
      <div class="mt-3 pl-10" v-else>
        <v-checkbox required :rules="textRules"
                    :disabled="(getApplicantFee != null && getApplicantFee.cleared) || !canEdit || isUtme" v-model="declaration"
                    :value="!canEdit" label="I hereby declare that information given in this form are
                                to the best of my knowledge checked and correctly provided"></v-checkbox>

      </div>
    </v-form>
    <v-card-actions class="mt-6">
      <v-spacer></v-spacer>
      <v-btn color="basic" class="white--text" @click="$emit('back')" depressed>{{  noHeader ? 'Back' : 'Previous' }}</v-btn>
      <v-btn color="error" depressed @click="$refs.form.reset()" v-if="canEdit && !isUtme && !noHeader && !programmeSelectionPending">Reset</v-btn>
      <v-btn color="primary" depressed @click="saveData"
        :disabled="!dataValid || !canEdit || (programmeSelectionPending && (!obj.programmeId || declaration !== true || !hasPassport))"
        v-if="canEdit">{{ programmeSelectionPending ? 'Confirm programme' : (noHeader ? 'Save' : 'Proceed') }}</v-btn>
      <v-btn color="info" depressed @click="success" v-if="getUser.programme != null && !programmeSelectionPending">Skip</v-btn>
    </v-card-actions>
    <loading-dialog :show="loading"></loading-dialog>
    <message-box :show="messageShow" :message="message" :type="messageType"
      v-on:close-message="messageShow = !messageShow"></message-box>
    <profile-picture-upload :show="showUploadPicture" v-on:close="showUploadPicture = !showUploadPicture"
      :user="getUser" v-on:uploaded="profilePictureUploaded"></profile-picture-upload>
  </div>
</template>


<script>
import { mapGetters } from "vuex";
import Defaults from "@/shared/Defaults";
import LoadingDialog from "@ui/components/LoadingDialog";
import MessageBox from "@ui/components/MessageBox";
import ValidationRules from "@/shared/ValidationRules";
import Api from "@/main/api";
import ErrorResolver from "@/shared/ErrorResolver";
import ProfilePictureUpload from "@/components/ProfilePictureUpload";
import StudentStatus from "@/shared/StudentStatus";
import {
  getProgrammeOptionChoices,
  isProgrammeOptionSelectionPending,
} from "@/shared/programmeOptionSelection";

export default {
  name: "ProgrammeAndPassport",
  props: {
    noHeader: Boolean
  },
  components: {
    ProfilePictureUpload,
    MessageBox,
    LoadingDialog,
  },

  data: () => ({
    obj: {},
    showUploadPicture: false,
    image: '',
    messageShow: false,
    loading: false,
    loadingProgrammes: false,
    message: '',
    messageType: '',
    dataValid: true,
    textRules: ValidationRules.textRules,
    declaration: null,
    photoUploaded: false,
    programmes: [],
    modeOfEntries: [],
    hidePhoto: false,
  }),

  methods: {
    success() {
      this.$emit("success")
    },

    profilePictureUploaded() {
      // this two lines are to detach the image back to the dom. thos makes the image to update
      // dont know why it's not updating without the lines
      this.hidePhoto = true
      this.image = null

      this.photoUploaded = true

      // attachaes bak the photo
      setTimeout(this.reloadPhoto, 1000)
    },

    reloadPhoto() {
      this.image = Defaults.api + '/profile-picture/fetch?user=' + this.getUser.userId;
      this.hidePhoto = false
    },

    check() {
      if (this.programmeSelectionPending) return;

      this.loading = true;
      Api.applicants.nextToProgrammeUpdate(this.getUser.id).then((res) => {
        this.loading = false;
        this.$store.commit('setCurrentUser', res.data);

      }).catch((err) => ErrorResolver.checkErrors(this, err))
        .finally(() => this.loading = false)
    },

    loadProgrammes() {
      this.loadingProgrammes = true
      const request = this.programmeSelectionPending
        ? Api.programmes.allForDepartment(this.getUser.programme.department.id)
        : Api.programmes.allAvailable(this.getUser.admission.session.programmeType.id)

      request
        .then(res => {
          this.programmes = this.programmeSelectionPending
            ? getProgrammeOptionChoices(res.data, this.getUser.programme, this.applicantProgrammeTypeId)
            : res.data
        })
        .catch(err => ErrorResolver.checkErrors(this, err))
        .finally(() => this.loadingProgrammes = false)
    },

    loadModeOfEntries() {
      if (this.getUser != null) {
        this.modeOfEntries = []
        Api.system.modeOfEntries(this.getUser.yearOfAdmission.programmeType.id)
          .then(res => this.modeOfEntries = res.data)
          .catch(err => ErrorResolver.checkErrors(this, err))
          .finally(() => this.loading = false)
      }
    },

    saveData() {
      const previousUserId = this.getUser.userId
      this.loading = true
      Api.applicants.programmeUpdate(this.getUser.id, this.obj)
        .then((res) => {
          this.message = 'Academic details updated'
          this.messageType = 'success'
          this.messageShow = true
          this.$store.commit("setCurrentUser", res.data);
          // check for applicant ID Change
          if(previousUserId !== res.data.userId){
            this.message = 'You have a new ID!!! Check the dashboard for it'
            this.messageType = 'success'
            this.messageShow = true
          }

          this.success()
        }).catch(err => ErrorResolver.checkErrors(this, err))
        .finally(() => this.loading = false)
    }
  },

  computed: {
    ...mapGetters(['getUser', 'getApplicantFee']),
    canEdit: function () {
      if (this.programmeSelectionPending) return true;
      // status still not upto application complete
      return this.getUser != null && (StudentStatus[this.getUser.studentStatus].value <= StudentStatus.APPLICATION_PENDING.value)
    },

    programmeSelectionPending: function () {
      return isProgrammeOptionSelectionPending(this.getUser)
    },

    currentDepartmentName: function () {
      return this.getUser?.programme?.department?.name || "your current department"
    },

    applicantProgrammeTypeId: function () {
      return this.getUser?.programme?.programmeType?.id ||
        this.getUser?.admission?.session?.programmeType?.id ||
        this.getUser?.yearOfAdmission?.programmeType?.id ||
        null
    },

    hasPassport: function () {
      return this.photoUploaded || this.getUser?.profilePicture != null
    },

    isUtme: function () {
      return this.getUser != null && this.getUser.admission.applicationType.autoLoadUtme
    }
  },

  watch: {
    getUser: function (val) {
      if (val != null) {
        if (this.programmeSelectionPending) {
          this.obj.programmeId = null
          this.declaration = null
        } else if (this.getUser.programme != null) {
          // this.success()
          // return
          this.obj.programmeId = this.getUser.programme.id
        }
        this.image = Defaults.api + '/profile-picture/fetch?user=' + val.userId;
        // mode of entries
        this.loadModeOfEntries()
      }
    }
  },

  created() {
    if (this.getUser != null) {
      this.image = Defaults.api + '/profile-picture/fetch?user=' + this.getUser.userId;
    }

    // load data
    if (this.getUser != null) {
      // load mode of entries
      this.loadModeOfEntries()
      // already uploaded
      if (this.getUser.programme != null) {
        if (this.programmeSelectionPending) {
          this.obj.programmeId = null
          this.declaration = null
        } else {
          this.obj.programmeId = this.getUser.programme.id
          this.declaration = true
        }
      }


      this.loadProgrammes(); //load programmes
      this.check();
    }
    this.noHeader && this.$emit('update-title', "Programme")
  }
}
</script>
